import { IncidentRosterPerson, InclusionReason } from '../Types/Incident';
import { PersonType } from '../Types/enums';
import { getBadgeTypeLabel } from './personTypeLabels';

/** Human-readable labels for roster source API enum values. */
const ROSTER_SOURCE_LABELS: Record<string, string> = {
  badgeData: 'Badged In',
  lobbyTrack: 'Lobby Track',
  assignedOffice: 'Assigned Office',
  manualAdd: 'Manual Add',
};

/**
 * Derive the roster source label for a person.
 * Maps API enum values to human-readable labels.
 * If isUnlisted, return "Manual Add".
 * Otherwise return undefined (no chip shown).
 */
export const getRosterSourceLabel = (person: IncidentRosterPerson): string | undefined => {
  if (person.isUnlisted) return 'Manual Add';
  if (person.rosterSource) {
    return ROSTER_SOURCE_LABELS[person.rosterSource] ?? person.rosterSource;
  }
  return undefined;
};

/**
 * Build a compact company/department display string.
 * Prefers companyName, falls back to practice.
 * Returns undefined if nothing meaningful is available.
 */
export const getCompanyDisplay = (person: IncidentRosterPerson): string | undefined => {
  const isTenant = person.personType?.toLowerCase().trim() === PersonType.Tenant;
  if (isTenant) {
    return person.companyName ? `Tenant \u00B7 ${person.companyName}` : 'Tenant';
  }
  if (person.companyName) return person.companyName;
  if (person.practice) return person.practice;
  return undefined;
};

/**
 * Build incident-day badge text for display.
 * Uses only incident-day/today data — never historical.
 *
 * Display rules (priority order):
 * A. Badge activity with location → "Last badge May 14, 2026: 9:25 AM · Main Lobby Entrance · Floor 1"
 * B. Badge activity without location → "Last badge May 14, 2026: 9:25 AM · Reader 3135 · Location unavailable"
 * C. Lobby Track → "Lobby Track check-in May 14, 2026: 8:15 AM · Menlo Park"
 * D. Assigned office employee, no badge → "Assigned office roster · No badge activity today"
 *    Only when isAssignedToIncidentOffice===true AND hasIncidentDayBadgeActivity===false
 * E. Manual add / unlisted → "Manual Add"
 * F. Badge Data source but badge details missing → "Badge Data source · Badge details unavailable"
 *    Prevents contradictory "No badge activity today" for badge-sourced records (tenants, etc.)
 * G. Fallback → "No badge activity today"
 */
export const getIncidentDayBadgeText = (person: IncidentRosterPerson): string | undefined => {
  const src = person.rosterSource;
  const badge = person.lastIncidentDayBadgeActivity;
  const lobby = person.lobbyTrackActivity;
  const isBadgeDataSource = src === 'badgeData'
    || (person.inclusionReasons != null && person.inclusionReasons.includes('badgeData'));

  // Rule E: Manual Add / unlisted
  if (src === 'manualAdd' || person.isUnlisted) {
    return 'Manual Add';
  }

  // Rule C: Lobby Track
  if (src === 'lobbyTrack' && lobby?.checkedInAtLocalDateTime) {
    const time = formatBadgeTimeShort(lobby.checkedInAtLocalDateTime);
    const date = formatBadgeDateShort(lobby.checkedInAtLocalDateTime);
    const parts = [`Lobby Track check-in ${date}: ${time}`];
    if (lobby.locationName) parts.push(lobby.locationName);
    return parts.join(' \u00B7 ');
  }
  // Legacy lobby track field
  if (person.lobbyTrackCheckInAtUtc) {
    const time = formatBadgeTimeShort(person.lobbyTrackCheckInAtUtc);
    const date = formatBadgeDateShort(person.lobbyTrackCheckInAtUtc);
    return `Lobby Track check-in ${date}: ${time}`;
  }

  // Rule A/B: Badge activity (new structured fields)
  if (badge?.badgeActivityAtLocalDateTime) {
    const time = formatBadgeTimeShort(badge.badgeActivityAtLocalDateTime);
    const date = formatBadgeDateShort(badge.badgeActivityAtLocalDateTime);
    const badgeLabel = getBadgeTypeLabel(badge.badgeType);
    const prefix = badgeLabel && badgeLabel !== 'Standard badge'
      ? `${badgeLabel} ${date}: ${time}`
      : `Last badge ${date}: ${time}`;
    const parts = [prefix];

    // Build location description from available fields
    const locationParts: string[] = [];
    if (badge.doorName) locationParts.push(badge.doorName);
    else if (badge.readerName) locationParts.push(badge.readerName);
    else if (badge.readerNumber) locationParts.push(`Reader ${badge.readerNumber}`);

    if (badge.floor) {
      locationParts.push(`Floor ${badge.floor.replace(/^Floor\s+/i, '')}`);
    } else if (badge.locationName) {
      // Only add locationName when it provides info not already in doorName/readerName
      const alreadyShown = locationParts[0]?.toLowerCase() ?? '';
      if (!alreadyShown.startsWith(badge.locationName.toLowerCase())) {
        locationParts.push(badge.locationName);
      }
    }

    if (locationParts.length > 0) {
      parts.push(...locationParts);
    } else {
      // Badge timestamp exists but no mapped location
      if (badge.readerNumber) parts.push(`Reader ${badge.readerNumber}`);
      parts.push('Location unavailable');
    }

    return parts.join(' \u00B7 ');
  }

  // Legacy badge fields
  if (person.lastBadgeAtUtc) {
    const time = formatBadgeTimeShort(person.lastBadgeAtUtc);
    const date = formatBadgeDateShort(person.lastBadgeAtUtc);
    const parts = [`Last badge ${date}: ${time}`];
    if (person.lastBadgeDoorName) parts.push(person.lastBadgeDoorName);
    else if (person.lastBadgeReaderName) parts.push(person.lastBadgeReaderName);
    if (person.lastBadgeLocation) parts.push(person.lastBadgeLocation);
    return parts.join(' \u00B7 ');
  }

  // Rule D: Assigned office with no badge — only for assigned-office employees
  if (person.isAssignedToIncidentOffice === true && person.hasIncidentDayBadgeActivity === false) {
    return 'Assigned Office \u00B7 No badge activity today';
  }

  // Rule F: Badge Data source but badge details missing — safe fallback
  // Prevents showing "No badge activity today" for tenants/others included via badge data
  if (isBadgeDataSource) {
    return 'Badged In · Badge details unavailable';
  }

  // Rule G: Fallback
  if (person.hasBadgeActivityToday === false || src) {
    return 'No badge activity today';
  }

  return 'No badge activity today';
};

const formatBadgeTimeShort = (localDatetime: string): string => {
  try {
    const d = new Date(localDatetime);
    return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  } catch {
    return '';
  }
};

/** Format a local datetime string to a readable date string (e.g. "May 14, 2026"). */
const formatBadgeDateShort = (localDatetime: string): string => {
  try {
    const d = new Date(localDatetime);
    return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  } catch {
    return '';
  }
};

/**
 * Check if a person matches a set of person type filter values.
 * An empty or undefined filter array means "All Types" (match all).
 */
export const personMatchesPersonType = (
  person: IncidentRosterPerson,
  selectedTypes?: string[],
): boolean => {
  if (!selectedTypes || selectedTypes.length === 0) return true;
  const pt = (person.personType ?? '').toLowerCase().trim();
  // isUnlisted persons match 'unlisted'
  if (person.isUnlisted && selectedTypes.includes(PersonType.Unlisted)) return true;
  return selectedTypes.includes(pt);
};

// ── Inclusion reason helpers ──

/** Human-readable labels for inclusion reason API enum values. */
const INCLUSION_REASON_LABELS: Record<string, string> = {
  assignedOffice: 'Assigned Office',
  badgeData: 'Badged In',
  lobbyTrack: 'Lobby Track',
  manualAdd: 'Manual Add',
};

/**
 * Get human-readable labels for a person's inclusion reasons.
 * Falls back to rosterSource if inclusionReasons is empty/missing.
 * If isUnlisted, always includes "Manual Add".
 */
export const getInclusionReasonLabels = (person: IncidentRosterPerson): string[] => {
  if (person.isUnlisted) return ['Manual Add'];

  const reasons = person.inclusionReasons;
  if (reasons && reasons.length > 0) {
    return reasons.map((r) => INCLUSION_REASON_LABELS[r] ?? r);
  }

  // Fall back to rosterSource
  if (person.rosterSource) {
    const label = ROSTER_SOURCE_LABELS[person.rosterSource] ?? person.rosterSource;
    return [label];
  }

  return [];
};

/** Check if a person has a specific inclusion reason. */
export const personHasInclusionReason = (
  person: IncidentRosterPerson,
  reason: InclusionReason,
): boolean => {
  const reasons = person.inclusionReasons;
  if (reasons && reasons.length > 0) {
    return reasons.includes(reason);
  }
  // Fall back to rosterSource
  return person.rosterSource === reason;
};

// ── Source filter ──

export type SourceFilter =
  | 'All'
  | 'badgeData'
  | 'lobbyTrack'
  | 'assignedOffice'
  | 'assignedOfficeNoBadge'
  | 'manualAdd';

export const SOURCE_FILTER_LABELS: Record<SourceFilter, string> = {
  All: 'All Sources',
  badgeData: 'Badged In',
  lobbyTrack: 'Lobby Track',
  assignedOffice: 'Assigned Office',
  assignedOfficeNoBadge: 'Assigned Office \u00B7 No Badge Today',
  manualAdd: 'Manual Add',
};

/** Check if a person matches the selected source filter. */
export const personMatchesSourceFilter = (
  person: IncidentRosterPerson,
  sourceFilter: SourceFilter,
): boolean => {
  if (sourceFilter === 'All') return true;

  if (sourceFilter === 'assignedOffice') {
    return person.isAssignedToIncidentOffice === true;
  }

  if (sourceFilter === 'assignedOfficeNoBadge') {
    return person.isAssignedToIncidentOffice === true
      && person.hasIncidentDayBadgeActivity === false;
  }

  if (sourceFilter === 'manualAdd') {
    return person.isUnlisted === true
      || personHasInclusionReason(person, 'manualAdd');
  }

  if (sourceFilter === 'lobbyTrack') {
    return personHasInclusionReason(person, 'lobbyTrack')
      || person.lobbyTrackActivity != null;
  }

  // badgeData
  if (sourceFilter === 'badgeData') {
    return person.hasIncidentDayBadgeActivity === true
      || personHasInclusionReason(person, 'badgeData');
  }

  return personHasInclusionReason(person, sourceFilter);
};
